import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Headphones,
  Printer,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  DollarSign,
  User,
  BookOpen,
  Search,
  Filter,
  TrendingUp,
  Package,
  Mail,
  Phone,
  MapPin,
  Edit,
  Save,
  X,
  Upload,
  Send,
  Truck,
  FileAudio,
  Download,
  Eye,
  Shield,
  MessageCircle
} from 'lucide-react';
import { serviceService } from '../../src/services/api';
import { useAuth } from '../../src/context/AuthContext';

// Replace the ServiceTypeTabs component with this simplified version
const ServiceTypeTabs = ({ activeTab, onTabChange, user }) => {
  return (
    <div className="flex border-b border-gray-200 mb-6">
      {[
        { id: 'audiobook', label: 'Audiobook Requests', icon: Headphones },
        { id: 'print', label: 'Print Requests', icon: Printer },
        { id: 'all', label: 'All Services', icon: Package },
        { id: 'support', label: 'Support Tickets', icon: MessageCircle },
      ].map(tab => (
        <button
          key={tab.id}
          onClick={() => onTabChange(tab.id)}
          className={`flex items-center px-6 py-4 border-b-2 text-sm font-semibold transition-colors ${
            activeTab === tab.id
              ? 'border-[#1d1d1d] text-[#1d1d1d]'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          <tab.icon className="h-4 w-4 mr-2" />
          {tab.label}
        </button>
      ))}
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const statusColors = {
    pending: 'bg-yellow-100 text-yellow-800 border border-yellow-200',
    reviewed: 'bg-blue-100 text-blue-800 border border-blue-200',
    quote_sent: 'bg-purple-100 text-purple-800 border border-purple-200',
    payment_pending: 'bg-orange-100 text-orange-800 border border-orange-200',
    payment_required: 'bg-orange-100 text-orange-800 border border-orange-200',
    payment_completed: 'bg-green-100 text-green-800 border border-green-200',
    approved: 'bg-green-100 text-green-800 border border-green-200',
    in_production: 'bg-indigo-100 text-indigo-800 border border-indigo-200',
    completed: 'bg-emerald-100 text-emerald-800 border border-emerald-200',
    shipped: 'bg-teal-100 text-teal-800 border border-teal-200',
    delivered: 'bg-emerald-100 text-emerald-800 border border-emerald-200',
    rejected: 'bg-red-100 text-red-800 border border-red-200',
    cancelled: 'bg-gray-100 text-gray-800 border border-gray-200',
  };
  
  const statusIcons = {
    pending: Clock,
    reviewed: AlertCircle,
    quote_sent: DollarSign,
    payment_pending: Clock,
    payment_required: DollarSign,
    payment_completed: CheckCircle,
    approved: CheckCircle,
    in_production: Package,
    completed: CheckCircle,
    shipped: Truck,
    delivered: CheckCircle,
    rejected: XCircle,
    cancelled: XCircle,
  };
  
  const Icon = statusIcons[status] || AlertCircle;
  const colorClass = statusColors[status] || statusColors.pending;
  
  return (
    <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium ${colorClass}`}>
      <Icon className="h-3 w-3 mr-1.5" />
      {status.replace('_', ' ').toUpperCase()}
    </span>
  );
};

const ServiceRequestCard = ({ request, type, onViewDetails, onAssign, user }) => {
  const isAudiobook = type === 'audiobook';
  const isAssignedToMe = request.admin_assigned_to === user?.id;
  const canAssign = !request.admin_assigned_to;
  const canManage = isAssignedToMe || user?.is_superuser;
  
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-all duration-200">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-start space-x-4">
          <div className="flex-shrink-0">
            <div className={`h-12 w-12 rounded-lg flex items-center justify-center ${
              isAudiobook ? 'bg-purple-100' : 'bg-green-100'
            }`}>
              {isAudiobook ? (
                <Headphones className="h-6 w-6 text-purple-600" />
              ) : (
                <Printer className="h-6 w-6 text-green-600" />
              )}
            </div>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-1">{request.book_title}</h3>
            <div className="flex items-center space-x-2 text-xs text-gray-600 mb-2">
              <User className="h-3 w-3" />
              <span>{request.author_name}</span>
              <span className="text-gray-400">•</span>
              <BookOpen className="h-3 w-3" />
              <span>SVP: {request.book_svp_id}</span>
            </div>
            <div className="flex items-center space-x-2">
              <StatusBadge status={request.status} />
              {request.payment_status && request.payment_status !== 'pending' && (
                <StatusBadge status={request.payment_status} />
              )}
            </div>
          </div>
        </div>
        
        <div className="text-right">
          {request.quote_amount ? (
            <>
              <div className="text-lg font-bold text-gray-900 mb-1">
                GHS {request.quote_amount}
              </div>
              <div className="text-xs text-gray-500">Quote Amount</div>
            </>
          ) : (
            <>
              <div className="text-lg font-bold text-gray-900 mb-1">—</div>
              <div className="text-xs text-gray-500">Quote Needed</div>
            </>
          )}
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <div className="text-xs text-gray-500 mb-1">Service Type</div>
          <div className="text-sm font-medium text-gray-900">
            {isAudiobook ? request.audio_format : request.print_type}
          </div>
        </div>
        <div>
          <div className="text-xs text-gray-500 mb-1">Requested</div>
          <div className="text-sm font-medium text-gray-900">
            {new Date(request.created_at).toLocaleDateString()}
          </div>
        </div>
      </div>
      
      <div className="flex items-center justify-between">
        <div className="text-xs text-gray-500">
          {request.admin_assigned_to ? (
            <div className="flex items-center">
              <span className="text-gray-600 font-medium">Assigned to: </span>
              <span className="ml-1">{request.assigned_admin_name}</span>
            </div>
          ) : (
            <span className="text-yellow-600 font-medium">Unassigned</span>
          )}
        </div>
        
        <div className="flex items-center space-x-2">
          {canAssign && (
            <button
              onClick={() => onAssign(request.id, type)}
              className="text-xs bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Assign to Me
            </button>
          )}
          <button
            onClick={() => onViewDetails(request, type)}
            className="text-xs bg-gray-100 text-gray-700 px-3 py-1.5 rounded-lg hover:bg-gray-200 transition-colors"
          >
            {canManage ? 'Manage' : 'View'}
          </button>
        </div>
      </div>
    </div>
  );
};

const ServiceDetailsModal = ({ request, type, isOpen, onClose, onUpdate, onSendQuote, user }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isSendingQuote, setIsSendingQuote] = useState(false);
  const [formData, setFormData] = useState({
    status: request?.status || '',
    admin_notes: request?.admin_notes || '',
    admin_priority: request?.admin_priority || 'medium',
    estimated_completion_date: request?.estimated_completion_date || '',
  });
  const [quoteData, setQuoteData] = useState({
    quote_amount: request?.quote_amount || '',
    admin_notes: request?.admin_notes || '',
    estimated_completion_date: request?.estimated_completion_date || '',
  });
  
  if (!isOpen || !request) return null;
  
  const isAudiobook = type === 'audiobook';
  const isAssignedToMe = request.admin_assigned_to === user?.id;
  const canManage = isAssignedToMe || user?.is_superuser;
  const canSendQuote = canManage && !request.quote_amount;
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    await onUpdate(request.id, type, formData);
    setIsEditing(false);
  };
  
  const handleSendQuote = async (e) => {
    e.preventDefault();
    setIsSendingQuote(true);
    try {
      await onSendQuote(request.id, type, quoteData);
      setIsSendingQuote(false);
    } catch (error) {
      setIsSendingQuote(false);
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className={`p-2 rounded-lg ${isAudiobook ? 'bg-purple-100' : 'bg-green-100'}`}>
              {isAudiobook ? (
                <Headphones className="h-5 w-5 text-purple-600" />
              ) : (
                <Printer className="h-5 w-5 text-green-600" />
              )}
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">{request.book_title}</h2>
              <p className="text-sm text-gray-600">by {request.author_name} • SVP: {request.book_svp_id}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <div className="p-6 max-h-[70vh] overflow-y-auto">
          {/* Request Information */}
          <div className="grid grid-cols-2 gap-6 mb-6">
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Author Contact</label>
                <div className="space-y-2">
                  <div className="flex items-center text-sm">
                    <Mail className="h-3 w-3 mr-2 text-gray-400" />
                    {request.author_email}
                  </div>
                  {request.contact_phone && (
                    <div className="flex items-center text-sm">
                      <Phone className="h-3 w-3 mr-2 text-gray-400" />
                      {request.contact_phone}
                    </div>
                  )}
                </div>
              </div>
              
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Service Details</label>
                <div className="space-y-2 text-sm">
                  {isAudiobook ? (
                    <>
                      <div>
                        <span className="text-gray-500">Format: </span>
                        <span className="font-medium">{request.audio_format}</span>
                      </div>
                      {request.narration_style && (
                        <div>
                          <span className="text-gray-500">Narration: </span>
                          <span className="font-medium">{request.narration_style}</span>
                        </div>
                      )}
                      {request.target_duration && (
                        <div>
                          <span className="text-gray-500">Duration: </span>
                          <span className="font-medium">{request.target_duration} min</span>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <div>
                        <span className="text-gray-500">Type: </span>
                        <span className="font-medium">{request.print_type}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Quantity: </span>
                        <span className="font-medium">{request.quantity}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Size: </span>
                        <span className="font-medium">{request.book_size}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Paper: </span>
                        <span className="font-medium">{request.paper_type}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Payment Status</label>
                <div className="space-y-2">
                  <StatusBadge status={request.payment_status} />
                  {request.quote_amount && (
                    <div className="text-sm">
                      <span className="text-gray-500">Quote Amount: </span>
                      <span className="font-bold text-green-600">GHS {request.quote_amount}</span>
                    </div>
                  )}
                  {request.paid_amount > 0 && (
                    <div className="text-sm">
                      <span className="text-gray-500">Paid: </span>
                      <span className="font-medium">GHS {request.paid_amount}</span>
                    </div>
                  )}
                </div>
              </div>
              
              {!isAudiobook && request.shipping_address && (
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Shipping Address</label>
                  <div className="flex items-start text-sm">
                    <MapPin className="h-3 w-3 mr-2 text-gray-400 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{request.shipping_address}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Description */}
          {request.description && (
            <div className="mb-6">
              <label className="block text-xs font-medium text-gray-500 mb-1">Description</label>
              <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded-lg">
                {request.description}
              </p>
            </div>
          )}
          
          {/* Admin Management Section */}
          {canManage && (
            <div className="border-t border-gray-200 pt-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-900">Admin Management</h3>
                <div className="flex items-center space-x-2">
                  {canSendQuote && (
                    <button
                      onClick={() => setIsSendingQuote(!isSendingQuote)}
                      className="text-sm text-green-600 hover:text-green-700 font-medium"
                    >
                      <Send className="h-4 w-4 inline mr-1" />
                      Send Quote
                    </button>
                  )}
                  <button
                    onClick={() => setIsEditing(!isEditing)}
                    className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                  >
                    <Edit className="h-4 w-4 inline mr-1" />
                    {isEditing ? 'Cancel Edit' : 'Edit Details'}
                  </button>
                </div>
              </div>
              
              {/* Send Quote Form */}
              {isSendingQuote && (
                <form onSubmit={handleSendQuote} className="mb-6 p-4 bg-green-50 rounded-lg border border-green-200">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">Send Quote to Author</h4>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Quote Amount (GHS)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={quoteData.quote_amount}
                        onChange={(e) => setQuoteData({...quoteData, quote_amount: e.target.value})}
                        className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2"
                        placeholder="0.00"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Notes for Author</label>
                      <textarea
                        value={quoteData.admin_notes}
                        onChange={(e) => setQuoteData({...quoteData, admin_notes: e.target.value})}
                        rows="2"
                        className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2"
                        placeholder="Explain the quote details..."
                      />
                    </div>
                    
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Estimated Completion</label>
                      <input
                        type="date"
                        value={quoteData.estimated_completion_date}
                        onChange={(e) => setQuoteData({...quoteData, estimated_completion_date: e.target.value})}
                        className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2"
                      />
                    </div>
                    
                    <div className="flex justify-end space-x-3">
                      <button
                        type="button"
                        onClick={() => setIsSendingQuote(false)}
                        className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={isSendingQuote}
                        className="px-4 py-2 text-sm text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50"
                      >
                        {isSendingQuote ? 'Sending...' : 'Send Quote'}
                      </button>
                    </div>
                  </div>
                </form>
              )}
              
              {/* Edit Form or Display */}
              {isEditing ? (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
                      <select
                        value={formData.status}
                        onChange={(e) => setFormData({...formData, status: e.target.value})}
                        className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2"
                      >
                        <option value="reviewed">Reviewed</option>
                        <option value="quote_sent">Quote Sent</option>
                        <option value="payment_pending">Payment Pending</option>
                        <option value="approved">Approved</option>
                        <option value="in_production">In Production</option>
                        <option value="completed">Completed</option>
                        <option value="shipped">Shipped</option>
                        <option value="rejected">Rejected</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Priority</label>
                      <select
                        value={formData.admin_priority}
                        onChange={(e) => setFormData({...formData, admin_priority: e.target.value})}
                        className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2"
                      >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                        <option value="urgent">Urgent</option>
                      </select>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Admin Notes</label>
                    <textarea
                      value={formData.admin_notes}
                      onChange={(e) => setFormData({...formData, admin_notes: e.target.value})}
                      rows="3"
                      className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2"
                      placeholder="Add notes for the team..."
                    />
                  </div>
                  
                  {isAudiobook && (
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Estimated Completion</label>
                      <input
                        type="date"
                        value={formData.estimated_completion_date}
                        onChange={(e) => setFormData({...formData, estimated_completion_date: e.target.value})}
                        className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2"
                      />
                    </div>
                  )}
                  
                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setIsEditing(false)}
                      className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                    >
                      <Save className="h-3 w-3 inline mr-1" />
                      Save Changes
                    </button>
                  </div>
                </form>
              ) : (
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Status: </span>
                    <StatusBadge status={request.status} />
                  </div>
                  <div>
                    <span className="text-gray-500">Priority: </span>
                    <span className="font-medium capitalize">{request.admin_priority}</span>
                  </div>
                  {request.admin_notes && (
                    <div className="col-span-2">
                      <span className="text-gray-500">Admin Notes: </span>
                      <p className="text-gray-700 bg-gray-50 p-3 rounded-lg mt-1">{request.admin_notes}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          
          {/* File Upload Section for Audiobook */}
          {isAudiobook && canManage && request.status === 'in_production' && (
            <div className="border-t border-gray-200 pt-6 mt-6">
              <h4 className="text-sm font-semibold text-gray-900 mb-3">Upload Audiobook</h4>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center justify-center w-full">
                  <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-blue-300 border-dashed rounded-lg cursor-pointer bg-blue-50 hover:bg-blue-100">
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      <Upload className="h-8 w-8 text-blue-400 mb-2" />
                      <p className="text-sm text-blue-500">Click to upload audiobook file</p>
                      <p className="text-xs text-blue-400 mt-1">MP3, M4A, FLAC, or WAV format</p>
                    </div>
                    <input type="file" className="hidden" accept=".mp3,.m4a,.flac,.wav" />
                  </label>
                </div>
              </div>
            </div>
          )}
          
          {/* Shipping Update Section for Print */}
          {!isAudiobook && canManage && request.status === 'shipped' && (
            <div className="border-t border-gray-200 pt-6 mt-6">
              <h4 className="text-sm font-semibold text-gray-900 mb-3">Shipping Information</h4>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Tracking Number</label>
                    <input
                      type="text"
                      defaultValue={request.tracking_number || ''}
                      className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2"
                      placeholder="Enter tracking number"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Shipping Carrier</label>
                    <input
                      type="text"
                      defaultValue={request.shipping_carrier || ''}
                      className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2"
                      placeholder="e.g., DHL, UPS, FedEx"
                    />
                  </div>
                </div>
                <button className="mt-4 px-4 py-2 text-sm text-white bg-green-600 rounded-lg hover:bg-green-700">
                  Update Shipping
                </button>
              </div>
            </div>
          )}
        </div>
        
        {/* Action Buttons */}
        <div className="flex justify-end space-x-3 p-6 border-t border-gray-200">
          {canManage && (
            <>
              <button className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">
                <Eye className="h-3 w-3 inline mr-1" />
                View Author Dashboard
              </button>
              {isAudiobook && request.audiobook_file && (
                <button className="px-4 py-2 text-sm text-white bg-purple-600 rounded-lg hover:bg-purple-700">
                  <Download className="h-3 w-3 inline mr-1" />
                  Download Audiobook
                </button>
              )}
              <button className="px-4 py-2 text-sm text-white bg-green-600 rounded-lg hover:bg-green-700">
                <CheckCircle className="h-3 w-3 inline mr-1" />
                Mark Complete
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default function ServiceManagement({ initialTab = 'all' }) {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(initialTab);
  const [filters, setFilters] = useState({
    status: '',
    assignedToMe: false,
  });
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const queryClient = useQueryClient();
  
  const isSuperAdmin = user?.is_superuser || (user?.user_type === 'admin' && user?.is_staff);

  const { data: serviceRequests, isLoading } = useQuery({
    queryKey: ['service-requests', activeTab, filters],
    queryFn: () => serviceService.getServiceRequests(activeTab, filters),
  });

  const { data: stats } = useQuery({
    queryKey: ['service-stats'],
    queryFn: () => serviceService.getServiceStats(),
  });

  const assignMutation = useMutation({
    mutationFn: ({ requestId, type }) => serviceService.assignRequest(requestId, type),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['service-requests'] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ requestId, type, data }) => serviceService.updateRequestStatus(requestId, type, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['service-requests'] });
      setShowDetailsModal(false);
    },
  });

  const sendQuoteMutation = useMutation({
    mutationFn: ({ requestId, type, data }) => serviceService.sendQuote(requestId, type, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['service-requests'] });
      alert('Quote sent successfully!');
    },
  });

  const handleAssign = (requestId, type) => {
    assignMutation.mutate({ requestId, type });
  };

  const handleViewDetails = (request, type) => {
    setSelectedRequest({ ...request, type });
    setShowDetailsModal(true);
  };

  const handleUpdate = (requestId, type, data) => {
    updateMutation.mutate({ requestId, type, data });
  };

  const handleSendQuote = (requestId, type, data) => {
    sendQuoteMutation.mutate({ requestId, type, data });
  };

  const requests = serviceRequests?.[activeTab === 'audiobook' ? 'audiobook_requests' : 
                       activeTab === 'print' ? 'print_requests' : 
                       'all_requests'] || [];

  // Filter requests for staff users
  const filteredRequests = isSuperAdmin ? requests : requests.filter(req => 
    req.admin_assigned_to === user?.id || req.status === 'pending'
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Service Management</h1>
          <p className="text-gray-600 font-medium">
            Manage audiobook and print service requests from authors
            {!isSuperAdmin && ' (Assigned to you only)'}
          </p>
        </div>
        <div className="flex items-center space-x-4">
          {isSuperAdmin && (
            <div className="text-right">
              <div className="text-sm text-gray-500">Total Revenue</div>
              <div className="text-2xl font-bold text-gray-900">
                GHS {((stats?.audiobook?.revenue || 0) + (stats?.print?.revenue || 0)).toFixed(2)}
              </div>
            </div>
          )}
          <div className={`p-2 rounded-lg ${isSuperAdmin ? 'bg-green-100' : 'bg-blue-100'}`}>
            <Shield className={`h-5 w-5 ${isSuperAdmin ? 'text-green-600' : 'text-blue-600'}`} />
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Pending Requests</p>
              <p className="text-2xl font-bold text-gray-900">
                {(stats?.audiobook?.pending || 0) + (stats?.print?.pending || 0)}
              </p>
            </div>
            <Clock className="h-8 w-8 text-yellow-500" />
          </div>
        </div>
        
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">In Production</p>
              <p className="text-2xl font-bold text-gray-900">
                {(stats?.audiobook?.in_production || 0) + (stats?.print?.in_production || 0)}
              </p>
            </div>
            <Package className="h-8 w-8 text-blue-500" />
          </div>
        </div>
        
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Completed</p>
              <p className="text-2xl font-bold text-gray-900">
                {(stats?.audiobook?.completed || 0) + (stats?.print?.shipped || 0)}
              </p>
            </div>
            <CheckCircle className="h-8 w-8 text-green-500" />
          </div>
        </div>
        
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Total Requests</p>
              <p className="text-2xl font-bold text-gray-900">
                {(stats?.audiobook?.total || 0) + (stats?.print?.total || 0)}
              </p>
            </div>
            <TrendingUp className="h-8 w-8 text-purple-500" />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="bg-white rounded-2xl shadow-xs border border-gray-200 overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <ServiceTypeTabs activeTab={activeTab} onTabChange={setActiveTab} user={user} />
            
            <div className="flex items-center space-x-4">
              <select
                value={filters.status}
                onChange={(e) => setFilters({...filters, status: e.target.value})}
                className="text-sm border border-gray-300 rounded-lg px-3 py-2"
              >
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="reviewed">Reviewed</option>
                <option value="quote_sent">Quote Sent</option>
                <option value="payment_pending">Payment Pending</option>
                <option value="approved">Approved</option>
                <option value="in_production">In Production</option>
              </select>
              
              {isSuperAdmin && (
                <label className="flex items-center text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={filters.assignedToMe}
                    onChange={(e) => setFilters({...filters, assignedToMe: e.target.checked})}
                    className="mr-2 rounded border-gray-300"
                  />
                  Assigned to me
                </label>
              )}
            </div>
          </div>
        </div>

        <div className="p-6">
          {isLoading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1d1d1d]"></div>
            </div>
          ) : filteredRequests.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredRequests.map(request => (
                <ServiceRequestCard
                  key={request.id}
                  request={request}
                  type={activeTab === 'all' ? request.type : activeTab}
                  onViewDetails={handleViewDetails}
                  onAssign={handleAssign}
                  user={user}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
                {activeTab === 'audiobook' ? (
                  <Headphones className="h-8 w-8 text-gray-400" />
                ) : activeTab === 'print' ? (
                  <Printer className="h-8 w-8 text-gray-400" />
                ) : (
                  <Package className="h-8 w-8 text-gray-400" />
                )}
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                No {activeTab} requests found
              </h3>
              <p className="text-gray-600">
                {isSuperAdmin 
                  ? 'No service requests found with current filters.'
                  : 'No service requests assigned to you. Ask a supervisor to assign you requests.'
                }
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Details Modal */}
      {selectedRequest && (
        <ServiceDetailsModal
          request={selectedRequest}
          type={selectedRequest.type}
          isOpen={showDetailsModal}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedRequest(null);
          }}
          onUpdate={handleUpdate}
          onSendQuote={handleSendQuote}
          user={user}
        />
      )}
    </div>
  );
}